import { useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { BidStatus, BidStatusName } from '../types/auction';
import { TradeState } from '../types/trade';
import BidService from '../../services/BidService';
import ReducerConstants from '../constants/reducers';
import { bidSlice } from '../../redux/slices/bid';

const useLiveBids = (
  tradeCardSummary,
  setTradeCardSummary,
  pendingActiveProxy,
  pendingActiveBid,
  bidModalRef,
  isTimerExtended,
  setIsTimerExtended
) => {
  const dispatch = useDispatch();
  const bidService = BidService.get();

  const [liveBid, user, bidList] = useSelector(
    state => [
      state?.[ReducerConstants.LIVE_BIDS][tradeCardSummary?.id],
      state?.auth?.user,
      state[ReducerConstants.BID_LIST]
    ],
    shallowEqual
  );

  const { bidContext, setBidContext, bidModalOpen } =
    bidModalRef?.current ?? {};

  useEffect(() => {
    const updateTradeCard = (liveTrade, liveBidEvent) => {
      let userProxyAmount = null;
      const {
        bidCount,
        isFloorPriceMet,
        hasFloorPrice,
        isCloseToFloorPrice,
        maxBidAmount,
        topBidder,
        tradeId,
        activeExpiryTime
      } = liveBidEvent;

      if (liveTrade && user) {
        let userBidStatus = liveTrade.auctionSummary?.userBidStatus;
        if (
          (userBidStatus === BidStatus.topBidder ||
            (liveTrade.auctionSummary?.proxyAmount < maxBidAmount &&
              userBidStatus === BidStatus.proxy)) &&
          user.id !== topBidder?.id
        ) {
          userBidStatus = BidStatus.outbid;
        } else if (user.id === topBidder?.id) {
          userBidStatus = BidStatus.topBidder;
        }

        // Set user's pending active proxy, after the live bid is received
        if (
          pendingActiveProxy?.tradeId === tradeId &&
          pendingActiveProxy?.proxyAmount > maxBidAmount
        ) {
          userProxyAmount = pendingActiveProxy.proxyAmount;
        }

        if (!userProxyAmount) {
          userProxyAmount =
            liveTrade.auctionSummary?.proxyAmount <= maxBidAmount
              ? null
              : liveTrade.auctionSummary?.proxyAmount;
        }

        // If the current trade is opened in a modal, and the data is different
        if (
          bidContext?.id === tradeId &&
          bidModalOpen &&
          liveTrade.auctionSummary?.maxBid !== maxBidAmount
        ) {
          setBidContext(currentBidContext => ({
            ...currentBidContext,
            currentBidAmount: maxBidAmount,
            numberOfBids: bidCount,
            bidStatus: userBidStatus,
            variant: BidStatusName[userBidStatus],
            proxyAmount: userProxyAmount,
            currentProxyAmount: userProxyAmount,
            activeExpiryTime,
            auctionSummary: {
              ...liveTrade.auctionSummary
            }
          }));

          bidService.getBidList(tradeId).then(bidListResponse => {
            dispatch(bidSlice.actions.setBidList(bidListResponse));
          });
        }

        return {
          ...liveTrade,
          auctionSummary: {
            ...liveTrade.auctionSummary,
            maxBid: maxBidAmount,
            proxyAmount: userProxyAmount,
            numBids: bidCount,
            userBidStatus,
            topBidderSummary: {
              ...liveTrade.auctionSummary?.topBidderSummary,
              ...liveBid.topBidder
            }
          },
          activeExpiryTime,
          tradeFloorPrice: {
            ...liveTrade.tradeFloorPrice,
            isFloorPriceMet,
            hasFloorPrice,
            isCloseToFloorPrice
          }
        };
      }

      return null;
    };

    if (tradeCardSummary && liveBid) {
      const updatedTrade = updateTradeCard(tradeCardSummary, liveBid);
      if (updatedTrade !== null) {
        setTradeCardSummary(updatedTrade);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [liveBid, pendingActiveProxy, user?.id]);

  useEffect(() => {
    if (
      liveBid?.isTimerExtended &&
      (!bidContext?.isTimerExtended || !isTimerExtended)
    ) {
      setBidContext(currentBidContext => ({
        ...currentBidContext,
        isTimerExtended: true
      }));
      setIsTimerExtended(true);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [liveBid?.isTimerExtended]);

  useEffect(() => {
    if (
      tradeCardSummary?.tradeState === TradeState.upcoming &&
      pendingActiveProxy?.tradeId === tradeCardSummary?.id
    ) {
      setTradeCardSummary(prevTrade => ({
        ...prevTrade,
        upcomingProxy: {
          ...prevTrade.upcomingProxy,
          bidAmount: pendingActiveProxy?.bidAmount,
          proxyAmount: pendingActiveProxy?.proxyAmount
        }
      }));
    }
    let userProxyAmount = null;
    // Set user's pending active proxy, after the live bid is received
    if (
      pendingActiveProxy?.tradeId === tradeCardSummary?.id &&
      pendingActiveProxy?.proxyAmount &&
      tradeCardSummary?.tradeState === TradeState.active
    ) {
      userProxyAmount = pendingActiveProxy.proxyAmount;
    }

    if (userProxyAmount) {
      setTradeCardSummary(prevTrade => ({
        ...prevTrade,
        auctionSummary: {
          ...prevTrade.auctionSummary,
          proxyAmount: userProxyAmount
        }
      }));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pendingActiveProxy]);

  // Handle for when user places a bid but is outbid by another set proxy as we got only 1 live bid for the last bid event
  useEffect(() => {
    let userBidStatus = tradeCardSummary?.auctionSummary?.userBidStatus;
    if (
      pendingActiveBid &&
      pendingActiveBid?.tradeId === tradeCardSummary?.id
    ) {
      userBidStatus =
        tradeCardSummary?.auctionSummary?.maxBid <= pendingActiveBid?.bidAmount
          ? BidStatus.topBidder
          : BidStatus.outbid;

      if (userBidStatus) {
        setTradeCardSummary(prevTrade => ({
          ...prevTrade,
          auctionSummary: {
            ...prevTrade.auctionSummary,
            userBidStatus
          }
        }));
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pendingActiveBid]);

  // Handle for when user places their first bid from list view modal but is outbid by a set proxy
  useEffect(() => {
    const updateTradeCard = (trade, liveBidEvent, bidListEvent) => {
      const { topBidder, tradeId, maxBidAmount } = liveBidEvent;
      let userBidStatus = trade?.auctionSummary?.userBidStatus;
      if (
        bidContext?.id === tradeId &&
        trade &&
        bidListEvent?.users.length &&
        bidListEvent?.users?.some(bidUsers => user?.id === bidUsers.id) &&
        bidListEvent?.users?.some(
          bidUsers => user?.companyId === bidUsers?.companyId
        )
      ) {
        if (topBidder?.id === user?.id) {
          userBidStatus = BidStatus.topBidder;
        } else {
          userBidStatus = BidStatus.outbid;
        }
        setBidContext(currentBidContext => ({
          ...currentBidContext,
          bidStatus: userBidStatus,
          variant: BidStatusName[userBidStatus],
          currentBidAmount: maxBidAmount
        }));
        return {
          ...trade,
          auctionSummary: {
            ...trade.auctionSummary,
            userBidStatus,
            maxBid: maxBidAmount,
            topBidderSummary: {
              ...trade.auctionSummary?.topBidderSummary,
              ...liveBid.topBidder
            }
          }
        };
      }
      return null;
    };

    if (tradeCardSummary && liveBid && bidList) {
      const updatedTrade = updateTradeCard(tradeCardSummary, liveBid, bidList);
      if (updatedTrade !== null) {
        setTradeCardSummary(updatedTrade);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [bidList]);
};

export default useLiveBids;
